import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);
dayjs.extend(relativeTime);

/**
 * The one place that turns dates into text, and the one dayjs instance
 * (plugins loaded) for date arithmetic. Nothing else imports dayjs.
 *
 * Two kinds of values flow through the apps:
 *  - calendar dates: 'YYYY-MM-DD' strings, picker Dates, birthdates, check-in.
 *    They have no timezone. `formatDate` / `toDateString` never shift them.
 *  - instants: ISO strings with a time part (createdAt, deadlines, startAt).
 *    `formatDateTime` / `formatTime` render them in the user's timezone.
 *    `formatDate` also converts a datetime string before taking its calendar day.
 *
 * Patterns are dayjs tokens (DD/MM/YYYY, dddd, z…).
 */

export { dayjs };

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const config = { timezone: dayjs.tz.guess() };

/** Wire the user setting here once it exists; nothing else should read the tz. */
export const configureDates = ({ timezone } = {}) => {
  config.timezone = timezone || dayjs.tz.guess();
};
export const getUserTimezone = () => config.timezone;

export const DATE = 'DD/MM/YYYY';
export const DATE_LONG = 'DD MMM YYYY';
export const DATE_US = 'MMM DD, YYYY';
export const MONTH_YEAR = 'MMMM YYYY';
export const WEEKDAY = 'dddd';
export const TIME = 'HH:mm';
export const DATE_TIME = 'DD/MM/YYYY HH:mm';
export const DATE_TIME_TZ = 'DD/MM/YYYY HH:mm z';
export const ISO_DATE = 'YYYY-MM-DD';

export const isDateOnly = (value) =>
  typeof value === 'string' && DATE_ONLY.test(value);

const isEmpty = (value) => value == null || value === '';

/** Instant (ISO string, Date, dayjs, epoch) → dayjs in the user tz, or null. */
const instant = (value) => {
  if (isEmpty(value)) return null;
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  return d.isValid() ? d.tz(config.timezone) : null;
};

/** Calendar date → dayjs at local midnight, or null. */
const calendar = (value) => {
  if (isEmpty(value)) return null;
  if (isDateOnly(value)) {
    const d = dayjs(value, ISO_DATE, true);
    return d.isValid() ? d : null;
  }
  if (typeof value === 'string') {
    const d = instant(value);
    return d && dayjs(d.format(ISO_DATE), ISO_DATE, true);
  }
  if (dayjs.isDayjs(value)) {
    // Its own zone's day (a hotel-tz value keeps the hotel's day), rebuilt at
    // browser midnight so pickers and toDate() don't slide it to the eve.
    return value.isValid()
      ? dayjs(value.format(ISO_DATE), ISO_DATE, true)
      : null;
  }
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d.startOf('day') : null;
  }
  return null;
};

export const parseDateOnly = (value) => calendar(value)?.toDate() ?? null;
export const parseInstant = (value) => instant(value)?.toDate() ?? null;

/** Calendar date → text. No timezone shift. */
export const formatDate = (value, pattern = DATE) =>
  calendar(value)?.format(pattern) ?? '';

/** Instant → text in the user's timezone. */
export const formatDateTime = (value, pattern = DATE_TIME) =>
  instant(value)?.format(pattern) ?? '';

export const formatTime = (value, pattern = TIME) =>
  formatDateTime(value, pattern);

/** Instant → "3 days ago" style text. */
export const formatRelative = (value) => instant(value)?.fromNow() ?? '';

/** Calendar date → 'YYYY-MM-DD' for the API, or null. */
export const toDateString = (value) => formatDate(value, ISO_DATE) || null;

/** Now, in the user's timezone. */
export const now = () => dayjs().tz(config.timezone);

/** Today as a calendar date string in the user's timezone. */
export const todayString = () => now().format(ISO_DATE);

/** Whole calendar days from `from` to `to` (to - from). */
export const daysBetween = (from, to) => {
  const a = calendar(from);
  const b = calendar(to);
  return a && b ? b.diff(a, 'day') : 0;
};

/** Full years elapsed since a calendar date (age), as of today. */
export const yearsSince = (value, at = new Date()) => {
  const a = calendar(value);
  const b = calendar(at);
  return a && b ? b.diff(a, 'year') : 0;
};
